/* ═══════════════════════════════════════════════════════════════
   Add Specs — Pull master specs into a project (formerly "Compile")
   One screen: pick project → pick sections → Add.
   Forma lives in the header.
   Adding snapshots each chosen master into a per-project spec — THE FORK
   (docs/MASTERS_LADDER_PLAN.md §1.1), composed by the one brain in
   `shared/spec-fork.ts`. This screen never flattens a master itself.
   ═══════════════════════════════════════════════════════════════ */

import React, { useState, useEffect, useMemo } from 'react';
import { ListPlus, X, FilePenLine } from 'lucide-react';
import { rem } from '../lib/rem';
import { Icon } from '../components/ui/Icon';
import { SearchableSelect } from '../components/SearchableSelect';
import { VariantPickCell } from '../components/VariantPickCell';
import { DataTable, type Column } from '../components/DataTable';
import { csiDivisionGroupLabel } from '../../shared/zone-layout';
import { isWeb } from '../lib/platform';
import { Button } from '../components/ui/button';
import { DivisionRail } from '../components/DivisionRail';
import { SpecService, nextDuplicateNumber } from '../services/specService';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { ProjectService } from '../services/projectService';
import { ProjectSpecService } from '../services/projectSpecService';
import { FormaService } from '../services/formaService';
import { OrgService } from '../services/orgService';
import { forkMasterBlocks, visibleComposedBlocks } from '../../shared/spec-fork';
import { EMPTY_PICKS, type ModulePicks } from '../../shared/spec-assemble';
import { listModuleInsertions } from '../../shared/spec-modules';
import { AssembleModulesDialog, canAssembleModules } from '../components/AssembleModulesDialog';
import { seedAddedSpecs } from '../../shared/related-sections';
import { reportError } from '../services/errorLog';
import type {
  Project,
  SpecMaster,
  StandardConfig,
  ProjectSpec,
  Organization,
} from '../../shared/types';
import { hasPermission } from '../../shared/permissions';
import { isArchived } from '../lib/archive';

interface AddSpecsProps {
  toast: (msg: string, isError?: boolean, opts?: { ms?: number }) => void;
  standards: StandardConfig[];
  /** Deep-link in (e.g. "+ Add Specs" from a project dashboard) → open
      straight into that project's section picker. Add Specs is project-first:
      it's only reachable from a project, never as a standalone destination. */
  deepLink?: { view: 'compile'; projectId?: string } | null;
  onDeepLinkConsumed?: () => void;
  /** Leave the cockpit — back to the project (or the Projects hub). */
  onExit?: (projectId?: string) => void;
  /** After adding, jump to the project's dashboard (detail view). */
  onOpenProjectDashboard?: (projectId: string) => void;
  userPermissions?: string[];
}

/** What the PM chose for one section. Add Specs only ever ADDS (SPEC_WORKFLOW
 *  §9–§11): `new` = first copy of this number, `replace` = archive the copy
 *  already in the project and fork a fresh one into its number, `dup` = a
 *  second copy under the next free `.01` number. */
type AddMode = 'new' | 'replace' | 'dup';
interface MasterSelection {
  masterId: string;
  mode: AddMode;
}

/** Variant picker sentinel — the office master itself, no client layer. */
const OFFICE_MASTER = '__office__';

const sameCsi = (a?: string, b?: string) =>
  !!(a || '').trim() && (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();

/** Fix-round: the project's membership for seeding the newcomers' Related
 *  sections is read FRESH immediately before the add, so a spec saved from
 *  another window in the meantime still counts. A failed fresh read falls
 *  back to the snapshot taken when the project was picked — the add must
 *  never block or half-finish on it. */
export function resolveFreshMembership(
  fresh: ProjectSpec[] | null,
  snapshot: ProjectSpec[]
): ProjectSpec[] {
  return fresh ?? snapshot;
}

/** Shared micro-interaction transition for hand-rolled toggles/pills. */
const FAST_COLORS =
  'background-color var(--duration-fast) var(--ease-out), color var(--duration-fast) var(--ease-out), border-color var(--duration-fast) var(--ease-out)';

function AddSpecs({
  toast,
  standards,
  deepLink,
  onDeepLinkConsumed,
  onExit,
  onOpenProjectDashboard,
  userPermissions,
}: AddSpecsProps) {
  const canRunCompile = hasPermission(userPermissions, 'compile.run');
  // Add + Replace write project specs; Replace also archives the old copy.
  const canCompileSpecs = hasPermission(userPermissions, 'project_specs.compile');
  const canArchiveSpecs = hasPermission(userPermissions, 'project_specs.archive');
  const [projects, setProjects] = useState<Project[]>([]);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [masters, setMasters] = useState<SpecMaster[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Active project (set by the deep-link from a project dashboard)
  const [activeProject, setActiveProject] = useState<Project | null>(null);

  // Selection
  const [selectedMasters, setSelectedMasters] = useState<MasterSelection[]>([]);
  const [selectedFormaId, setSelectedFormaId] = useState('');

  // Existing project specs (if project was already populated before)
  const [existingSpecs, setExistingSpecs] = useState<ProjectSpec[]>([]);
  const [adding, setAdding] = useState(false);
  // Variant chosen per master at add time (layer id; OFFICE_MASTER = none).
  const [variantPick, setVariantPick] = useState<Record<string, string>>({});
  // Module picks per master (row state only — nothing is saved until Add).
  const [modulePick, setModulePick] = useState<Record<string, ModulePicks>>({});
  // Modules dialog: one master (row pill) or the whole cart (primary button).
  const [assembleFor, setAssembleFor] = useState<null | { ids: string[]; thenAdd: boolean }>(null);
  const canModules = canAssembleModules(userPermissions);
  // "Already in the project" dialog target.
  const [inProjectFor, setInProjectFor] = useState<SpecMaster | null>(null);

  // Division quick-filter (left of the table; null = all divisions)
  const [divFilter, setDivFilter] = useState<string | null>(null);

  // Rename tool
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameFolder, setRenameFolder] = useState('');
  const [renameFormat, setRenameFormat] = useState('## ## ##');
  const [renamePreview, setRenamePreview] = useState<
    Array<{ original: string; renamed: string; error?: string }>
  >([]);
  const [renameRunning, setRenameRunning] = useState(false);
  const [renameDone, setRenameDone] = useState(false);

  useEffect(() => {
    loadData();
  }, []);
  useEffect(() => {
    const handler = () => loadData();
    window.addEventListener('specforma-sync-complete', handler);
    return () => window.removeEventListener('specforma-sync-complete', handler);
  }, []);

  // Esc closes the rename modal (a11y — overlays dismiss on Escape).
  useEffect(() => {
    if (!showRenameModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowRenameModal(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showRenameModal]);

  // Deep-link in: "+ Add Specs" from a project dashboard opens straight into
  // that project's picker. Wait for projects to load, match by id, consume once.
  useEffect(() => {
    if (!deepLink || deepLink.view !== 'compile' || !deepLink.projectId) return;
    if (projects.length === 0) return;
    const proj = projects.find((p) => p.id === deepLink.projectId);
    if (proj) selectProject(proj);
    onDeepLinkConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLink, projects]);

  async function loadData() {
    // Settled, not all-or-nothing — whatever loads still renders (DESIGN §5).
    const [p, m, o] = await Promise.allSettled([
      ProjectService.list(),
      SpecService.listMasters(),
      OrgService.list(),
    ]);
    if (p.status === 'fulfilled') setProjects(p.value);
    if (m.status === 'fulfilled') setMasters(m.value);
    if (o.status === 'fulfilled') setOrgs(o.value);
    const failed = [p, m, o].some((r) => r.status === 'rejected');
    setLoadError(
      failed
        ? "Some data didn't load. Check your connection — it reloads automatically when sync completes."
        : null
    );
    setLoading(false);
  }

  // ─── Pick or create project ───
  function selectProject(proj: Project) {
    const resolved = JSON.parse(JSON.stringify(proj));
    if (!resolved.clientName && resolved.clientOrgId) {
      resolved.clientName = orgs.find((o) => o.id === resolved.clientOrgId)?.name;
    }
    setActiveProject(resolved);
    // Auto-select ONLY a forma explicitly applied to this project: an
    // attached master, or the source master of the project's forma copy.
    // Anything else (including client-name matches) requires the user to
    // pick — firms keep several formas per client, silent guesses pick wrong.
    const attachedForma = standards.find(
      (s: any) => s.attachedProjectId === resolved.id && !s.projectId
    );
    if (attachedForma) {
      setSelectedFormaId(attachedForma.id);
    } else {
      FormaService.list()
        .then((all: any[]) => {
          const copy = all.find((f: any) => f.projectId === resolved.id && !f.specId);
          const srcId = copy?.sourceFormaId;
          setSelectedFormaId(srcId && standards.some((s: any) => s.id === srcId) ? srcId : '');
        })
        .catch(() => setSelectedFormaId(''));
    }
    // Load existing project specs and pre-select what's already there
    ProjectSpecService.listByProject(proj.id)
      .then((specs) => {
        setExistingSpecs(specs);
        // Add-only: nothing is pre-ticked. A spec already in the project shows
        // an "In project" state instead (removal = archive in the Specs table).
        setSelectedMasters([]);
      })
      .catch(() => setExistingSpecs([]));
  }

  // ─── Selection helpers ───
  /** Live (non-archived) project specs that already hold this master's number. */
  function inProjectSpecs(m: SpecMaster): ProjectSpec[] {
    return existingSpecs.filter(
      (s) => !isArchived(s) && (s.masterId === m.id || sameCsi(s.csiNumber, m.csiNumber))
    );
  }

  function toggleMaster(masterId: string) {
    const exists = selectedMasters.some((s) => s.masterId === masterId);
    if (exists) {
      setSelectedMasters((prev) => prev.filter((s) => s.masterId !== masterId));
      return;
    }
    const master = masters.find((m) => m.id === masterId);
    if (master && inProjectSpecs(master).length > 0) {
      setInProjectFor(master);
      return;
    }
    setSelectedMasters((prev) => [...prev, { masterId, mode: 'new' }]);
  }

  function chooseInProject(mode: 'replace' | 'dup') {
    if (!inProjectFor) return;
    const id = inProjectFor.id;
    setSelectedMasters((prev) => [
      ...prev.filter((s) => s.masterId !== id),
      { masterId: id, mode },
    ]);
    setInProjectFor(null);
  }

  function isSelected(masterId: string) {
    return selectedMasters.some((s) => s.masterId === masterId);
  }

  const selectedIdSet = useMemo(
    () => new Set(selectedMasters.map((s) => s.masterId)),
    [selectedMasters]
  );
  function onPickSelectionChange(ids: Set<string>) {
    const haveIds = new Set(selectedMasters.map((s) => s.masterId));
    const addedIds = [...ids].filter((id) => !haveIds.has(id));
    const blocked: SpecMaster[] = [];
    const fresh: MasterSelection[] = [];
    for (const id of addedIds) {
      const m = masters.find((x) => x.id === id);
      if (m && inProjectSpecs(m).length > 0) blocked.push(m);
      else fresh.push({ masterId: id, mode: 'new' });
    }
    // One section ticked on its own that is already here -> ask. A bulk tick
    // (select all) just skips what is already in the project.
    if (blocked.length === 1 && addedIds.length === 1) setInProjectFor(blocked[0]);
    setSelectedMasters((prev) => [...prev.filter((s) => ids.has(s.masterId)), ...fresh]);
  }

  // ─── Add to project (snapshot each master into a project spec) ───
  /** Primary button: Modules stepper over the cart first (when allowed), then the save loop. */
  function addToProject() {
    if (!activeProject || selectedMasters.length === 0) return;
    if (canModules) setAssembleFor({ ids: selectedMasters.map((s) => s.masterId), thenAdd: true });
    else void runAddToProject(modulePick);
  }

  /** The layer picked for a master at add time (undefined = Office Master). */
  function layerFor(master: SpecMaster) {
    const pick = variantPick[master.id];
    return pick && pick !== OFFICE_MASTER
      ? (master.layers || []).find((l) => l.id === pick)
      : undefined;
  }

  async function runAddToProject(picksByMaster: Record<string, ModulePicks>) {
    if (!activeProject || selectedMasters.length === 0) return;
    setAdding(true);
    const now = new Date().toISOString();

    try {
      // Specs-style forma application: a MASTER forma picked here is applied
      // to the project as a COPY (idempotent — reuses the project's existing
      // copy of the same master). New specs bind to the copy, so edits inside
      // the project never touch the master.
      let effectiveFormaId = selectedFormaId || undefined;
      const pickedMaster = standards.find((s: any) => s.id === selectedFormaId && !s.projectId);
      if (pickedMaster) {
        try {
          const copy = await FormaService.ensureProjectCopy(pickedMaster, {
            id: activeProject.id,
            number: (activeProject as any).projectNumber,
            name: activeProject.name,
          });
          effectiveFormaId = copy.id;
        } catch (err) {
          // Once per run, not once per spec.
          console.warn('[add-specs] project forma copy failed, binding master:', err);
          toast('Project forma not copied \u2014 using the master forma', false, { ms: 6000 });
        }
      }
      // Fresh membership first: numbers taken (for `.01`) and what Replace archives.
      let freshSpecs: ProjectSpec[] | null = null;
      try {
        freshSpecs = await ProjectSpecService.listByProject(activeProject.id);
      } catch (err) {
        reportError('AddSpecs:freshMembership', err);
      }
      const membership = resolveFreshMembership(freshSpecs, existingSpecs).filter(
        (s) => !isArchived(s)
      );

      const added: ProjectSpec[] = [];
      const toArchive: ProjectSpec[] = [];
      const takenNumbers: Array<{ csiNumber?: string }> = [...membership];
      for (const sel of selectedMasters) {
        const master = masters.find((m) => m.id === sel.masterId);
        if (!master) continue;
        const holders = membership.filter(
          (s) => s.masterId === master.id || sameCsi(s.csiNumber, master.csiNumber)
        );
        // Replace archives ONLY the copy sitting at this exact section number —
        // an earlier `.01` copy of the same master stays.
        const exactHolders = holders.filter((s) => sameCsi(s.csiNumber, master.csiNumber));

        // A number already in the project is never silently re-forked: it only
        // gets here through an explicit Replace / Add as .01 answer.
        let csiNumber = master.csiNumber;
        if (holders.length > 0) {
          if (sel.mode === 'replace') {
            if (!canArchiveSpecs) continue;
            toArchive.push(...exactHolders);
          } else if (sel.mode === 'dup') {
            csiNumber = nextDuplicateNumber(takenNumbers, master.csiNumber || '');
          } else {
            continue;
          }
        }

        // Variant chosen at add time, frozen after (the fork records which).
        const layer = layerFor(master);

        // New project spec — THE FORK. One brain composes it
        // (src/shared/spec-fork.ts -> compose + toExportBlocks).
        const flatBlocks = forkMasterBlocks(
          master,
          layer,
          picksByMaster[master.id] ?? EMPTY_PICKS,
          selectedForma?.id ? selectedForma : null,
          // Sync D1: Add to Project keeps module identity (the stamp rail).
          { keepModuleStamps: true }
        );

        const projectSpec: ProjectSpec = {
          id: 'ps-' + Date.now().toString(36) + Math.random().toString(36).substr(2, 6),
          projectId: activeProject.id,
          masterId: master.id,
          masterVersion: master.version,
          formaId: effectiveFormaId,
          csiNumber,
          title: master.title,
          description: master.description,
          coreContent: flatBlocks,
          // The merge base: exactly what was forked, before any project edit.
          syncBase: JSON.parse(JSON.stringify(flatBlocks)) as typeof flatBlocks,
          systems: JSON.parse(JSON.stringify(master.systems || [])),
          variants: [],
          ...(layer ? { variantId: layer.id } : {}),
          compiledAt: now,
          lastEdited: now,
        };

        takenNumbers.push({ csiNumber });
        added.push(projectSpec);
      }

      // Related sections (hook 3a): the whole add is ONE moment. Each new
      // spec's Related sections block starts with every section the project
      // holds once this add lands switched ON — batch-mates included. Specs
      // already in the project are left alone, so the newcomers reach their
      // pickers switched OFF. Specs being replaced no longer count.
      const archiveIds = new Set(toArchive.map((s) => s.id));
      const keptSpecs = membership.filter((s) => !archiveIds.has(s.id));
      for (const projectSpec of seedAddedSpecs(added, [...keptSpecs, ...added])) {
        await ProjectSpecService.save(projectSpec);
      }

      // Replace: the old copy is ARCHIVED (restorable from the Specs table),
      // only after the fresh one is safely saved.
      const archiveFailed: string[] = [];
      for (const old of toArchive) {
        try {
          await ProjectSpecService.setArchived(old.id, true);
        } catch (err) {
          reportError('AddSpecs:archiveReplaced', err);
          archiveFailed.push(old.csiNumber || old.title || 'a spec');
        }
      }

      // Update project record with master list (add-only, never shrinks)
      activeProject.masters = Array.from(
        new Set([...(activeProject.masters || []), ...added.map((a) => a.masterId)])
      );
      activeProject.updatedAt = new Date().toISOString();
      await ProjectService.save(activeProject);

      if (archiveFailed.length > 0) {
        toast(
          `Added, but the old copy of ${archiveFailed.join(', ')} wasn't archived. Archive it from the project's Specs.`,
          true
        );
      } else {
        toast('Specs added to project');
      }
      const updatedSpecs = await ProjectSpecService.listByProject(activeProject.id);
      setExistingSpecs(updatedSpecs);
      await loadData();

      if (onOpenProjectDashboard) onOpenProjectDashboard(activeProject.id);
    } catch (err: any) {
      toast('Failed to add specs: ' + (err?.message || err), true);
    } finally {
      setAdding(false);
    }
  }

  // ─── Rename spec files tool ───
  async function pickRenameFolder() {
    const folder = await window.api.selectFolderDialog();
    if (!folder) return;
    setRenameFolder(folder);
    setRenameDone(false);
    setRenamePreview([]);
  }

  async function executeRename() {
    if (!renameFolder) return;
    setRenameRunning(true);
    try {
      const results = await window.api.renameSpecFiles(renameFolder, renameFormat);
      setRenamePreview(results);
      setRenameDone(true);
      const renamed = results.filter((r) => r.original !== r.renamed && !r.error).length;
      const errors = results.filter((r) => r.error).length;
      if (renamed > 0)
        toast(
          `Renamed ${renamed} file${renamed !== 1 ? 's' : ''}${errors > 0 ? ` (${errors} error${errors !== 1 ? 's' : ''})` : ''}`
        );
      else if (errors > 0)
        toast(`${errors} error${errors !== 1 ? 's' : ''} — no files renamed`, true);
      else toast('All files already match the target format');
    } catch (err: any) {
      toast('Rename failed: ' + err.message, true);
    }
    setRenameRunning(false);
  }

  // ─── Derived: live counts ───
  const replaceCount = selectedMasters.filter((s) => s.mode === 'replace').length;

  // Archived masters drop out of the picker — except one already in this
  // project or already checked, so nothing already chosen disappears.
  const pickableMasters = useMemo(
    () =>
      masters.filter(
        (m) =>
          !isArchived(m) ||
          existingSpecs.some((e) => e.masterId === m.id) ||
          selectedMasters.some((s) => s.masterId === m.id)
      ),
    [masters, existingSpecs, selectedMasters]
  );

  // ─── CSI divisions (quick-filter rail above the table) ───
  const divisions = useMemo(() => {
    const map = new Map<string, number>();
    pickableMasters.forEach((m) => {
      const d = (m.csiNumber || '').split(' ')[0];
      if (d) map.set(d, (map.get(d) || 0) + 1);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [pickableMasters]);

  const railDivisions = useMemo(() => {
    const names = new Map<string, string>();
    SpecService.getDivisions().forEach((d: any) => names.set(d.code, d.name));
    return divisions.map(([code, count]) => ({ code, name: names.get(code) || '', count }));
  }, [divisions]);

  // Narrow windows: rail drops to codes-only so the table still fits (DESIGN §3.1).
  const [railCompact, setRailCompact] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 1500
  );
  useEffect(() => {
    const onResize = () => setRailCompact(window.innerWidth < 1500);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const shownMasters = useMemo(
    () =>
      divFilter
        ? pickableMasters.filter((m) => (m.csiNumber || '').split(' ')[0] === divFilter)
        : pickableMasters,
    [pickableMasters, divFilter]
  );

  // ─── Rename Modal ───
  const renameModal = (
    <div
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) setShowRenameModal(false);
      }}
      style={overlayStyle}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Rename spec files"
        style={modalStyle}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div
          style={{
            fontWeight: 700,
            fontSize: 'var(--font-size-lg)',
            marginBottom: 'var(--space-1)',
          }}
        >
          Rename Spec Files
        </div>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--tm)',
            marginBottom: 'var(--space-4)',
            lineHeight: 1.5,
          }}
        >
          Select a folder of .docx/.pdf spec files and rename them to match a spec number format.
          Files must follow the pattern:{' '}
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--font-size-sm)' }}>
            [digits] - [name].docx
          </span>
        </div>

        {/* Folder picker */}
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <div
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              color: 'var(--tm)',
              marginBottom: 'var(--space-1)',
            }}
          >
            Folder
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <div
              style={{
                flex: 1,
                padding: 'var(--space-2) var(--space-3)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--bd)',
                background: 'var(--ib)',
                color: renameFolder ? 'var(--tx)' : 'var(--tm)',
                fontSize: 'var(--font-size-sm)',
                fontFamily: 'var(--font-mono)',
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {renameFolder || 'No folder selected...'}
              </span>
            </div>
            <Button variant="outline" onClick={pickRenameFolder}>
              Browse
            </Button>
          </div>
        </div>

        {/* Format picker */}
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <div
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              color: 'var(--tm)',
              marginBottom: 'var(--space-1)',
            }}
          >
            Target Format
          </div>
          <div
            style={{
              display: 'flex',
              gap: 'var(--space-2)',
              flexWrap: 'wrap',
              marginBottom: 'var(--space-2)',
            }}
          >
            {['## ## ##', '## ####', '######', '##-##-##', '##-####'].map((fmt) => (
              <button
                key={fmt}
                onClick={() => setRenameFormat(fmt)}
                aria-pressed={renameFormat === fmt}
                style={{
                  padding: 'var(--space-2) var(--space-3)',
                  borderRadius: 'var(--radius-sm)',
                  border: renameFormat === fmt ? '1px solid var(--ac)' : '1px solid var(--bd)',
                  background: renameFormat === fmt ? 'rgba(var(--acr),0.08)' : 'transparent',
                  color: renameFormat === fmt ? 'var(--ac)' : 'var(--tm)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  fontFamily: 'var(--font-mono)',
                  cursor: 'pointer',
                  transition: FAST_COLORS,
                }}
              >
                {fmt}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            <label
              htmlFor="rename-custom-format"
              style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}
            >
              Custom:
            </label>
            <input
              id="rename-custom-format"
              value={renameFormat}
              onChange={(e) => setRenameFormat(e.target.value)}
              style={{
                width: rem(120),
                padding: 'var(--space-2)',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--bd)',
                background: 'var(--ib)',
                color: 'var(--tx)',
                fontSize: 'var(--font-size-sm)',
                fontFamily: 'var(--font-mono)',
              }}
            />
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>
              Use # for digits
            </span>
          </div>
        </div>

        {/* Results */}
        {renamePreview.length > 0 && (
          <div
            style={{
              marginBottom: 'var(--space-4)',
              maxHeight: rem(200),
              overflow: 'auto',
              background: 'var(--bg)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--bd)',
            }}
          >
            {renamePreview.map((r, i) => (
              <div
                key={i}
                style={{
                  padding: 'var(--space-2) var(--space-3)',
                  borderBottom: '1px solid var(--bd)',
                  fontSize: 'var(--font-size-sm)',
                  fontFamily: 'var(--font-mono)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                }}
              >
                {r.error ? (
                  <span style={{ color: 'var(--dg)' }}>
                    {r.original} — {r.error}
                  </span>
                ) : r.original === r.renamed ? (
                  <span style={{ color: 'var(--tm)' }}>{r.original} (no change)</span>
                ) : (
                  <>
                    <span style={{ color: 'var(--tm)', textDecoration: 'line-through' }}>
                      {r.original}
                    </span>
                    <span style={{ color: 'var(--ac)' }}>&rarr;</span>
                    <span style={{ color: 'var(--ok)' }}>{r.renamed}</span>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
          <Button variant="outline" onClick={() => setShowRenameModal(false)}>
            {renameDone ? 'Close' : 'Cancel'}
          </Button>
          {!renameDone && (
            <Button onClick={executeRename} disabled={!renameFolder || renameRunning}>
              {renameRunning ? 'Renaming...' : 'Rename Files'}
            </Button>
          )}
          {renameDone && renameFolder && (
            <Button variant="outline" onClick={() => window.api.openFolder(renameFolder)}>
              Open Folder
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════
  // No active project — Add Specs is project-first, reached only via the
  // "+ Add Specs" entry on a project dashboard. This state is transient
  // (the deep-link is resolving) or a stray landing; send them to Projects.
  // ═══════════════════════════════════════════════════════════════
  // Seeded module insert ids per master — recomputed only when masters or layer picks change.
  const seededByMaster = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const m of masters) {
      out[m.id] = listModuleInsertions(visibleComposedBlocks(m, layerFor(m))).map(
        (i) => i.insertId
      );
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masters, variantPick]);

  if (!activeProject) {
    const resolving = !!deepLink?.projectId;
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--space-4)',
          color: 'var(--tm)',
        }}
      >
        {resolving ? (
          <div style={{ fontSize: 'var(--font-size-base)' }}>Opening project…</div>
        ) : (
          <>
            <div style={{ fontSize: 'var(--font-size-md)' }}>
              Add specs to a project from its dashboard.
            </div>
            <Button onClick={() => onExit?.()}>Go to Projects</Button>
          </>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // SCREEN 2 — Add sections to the active project (single screen)
  // ═══════════════════════════════════════════════════════════════
  const formaOptions = standards
    .filter((s) => !isArchived(s) || s.id === selectedFormaId)
    .map((s) => ({
      value: s.id,
      label:
        (s.docSettings?.standardName || 'Untitled') +
        (s.docSettings?.clientName ? ` — ${s.docSettings.clientName}` : ''),
    }));

  const columns: Column<SpecMaster>[] = [
    {
      key: 'csiNumber',
      label: 'Section',
      width: 84,
      maxWidth: 84,
      getValue: (m) => m.csiNumber || '',
      render: (m) => (
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--font-size-base)',
            color: 'var(--ac)',
            fontWeight: 700,
          }}
        >
          {m.csiNumber || '—'}
        </span>
      ),
    },
    {
      key: 'title',
      label: 'Title',
      width: 168,
      maxWidth: 168,
      getValue: (m) => m.title || '',
      render: (m) => <span style={{ fontWeight: 500 }}>{m.title || 'Untitled'}</span>,
    },
    {
      key: 'variant',
      label: 'Variant',
      width: 150,
      maxWidth: 150,
      sortable: false,
      render: (m) => (
        <VariantPickCell
          layers={m.layers || []}
          value={variantPick[m.id]}
          officeMaster={OFFICE_MASTER}
          label={m.csiNumber || m.title || ''}
          onChange={(v) => setVariantPick((prev) => ({ ...prev, [m.id]: v }))}
        />
      ),
    },
    ...(canModules
      ? [
          {
            key: 'modules',
            label: 'Modules',
            width: 108,
            maxWidth: 108,
            sortable: false,
            render: (m: SpecMaster) => {
              const picks = modulePick[m.id] ?? EMPTY_PICKS;
              const excluded = new Set(picks.excludeInsertIds);
              const n =
                (seededByMaster[m.id] ?? []).filter((id) => !excluded.has(id)).length +
                picks.include.length;
              return (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAssembleFor({ ids: [m.id], thenAdd: false });
                  }}
                >
                  {n > 0 ? `Modules · ${n}` : 'Modules'}
                </Button>
              );
            },
          } as Column<SpecMaster>,
        ]
      : []),
    {
      key: 'status',
      label: 'Status',
      width: 92,
      maxWidth: 92,
      sortable: false,
      render: (m) => {
        const sel = selectedMasters.find((s) => s.masterId === m.id);
        if (sel) {
          if (sel.mode === 'replace')
            return <span style={badge('rgba(var(--acr),0.1)', 'var(--ac)')}>Replace</span>;
          if (sel.mode === 'dup')
            return <span style={badge('rgba(var(--okr),0.1)', 'var(--ok)')}>Add as .01</span>;
          return <span style={badge('rgba(var(--okr),0.1)', 'var(--ok)')}>Add</span>;
        }
        if (inProjectSpecs(m).length > 0)
          return <span style={badge('rgba(var(--acr),0.1)', 'var(--ac)')}>In project</span>;
        return (
          <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>{'\u2014'}</span>
        );
      },
    },
    {
      key: 'version',
      label: 'Ver',
      width: 52,
      maxWidth: 52,
      getValue: (m) => m.version || 0,
      render: (m) => (
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>v{m.version}</span>
      ),
    },
  ];

  const canAddSpecs = canRunCompile && canCompileSpecs;
  const selectedForma = standards.find((s) => s.id === selectedFormaId);
  const clientName = activeProject.clientName;
  const formaIsClientMatch =
    !!clientName &&
    !!selectedForma?.docSettings?.clientName &&
    selectedForma.docSettings.clientName.toLowerCase() === clientName.toLowerCase();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* ── Header: just back + project identity (forma + Add live in the cart) ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-4)',
          padding: 'var(--space-3) var(--space-5)',
          borderBottom: '1px solid var(--bd)',
          background: 'var(--sf)',
          flexShrink: 0,
        }}
      >
        <Button
          variant="outline"
          onClick={() => onExit?.(activeProject.id)}
          title="Back to the project dashboard"
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back
        </Button>

        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 'var(--font-size-lg)',
              fontWeight: 700,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {activeProject.name || 'Untitled'}
          </div>
          {(activeProject.projectNumber || clientName) && (
            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)', marginTop: rem(2) }}>
              {activeProject.projectNumber ? activeProject.projectNumber + ' · ' : ''}
              {clientName || ''}
            </div>
          )}
        </div>

        <div style={{ flex: 1 }} />

        {/* Rename Spec Files — batch file-naming utility (folder of .docx/.pdf).
            Desktop only: it works on a local folder. */}
        {!isWeb && (
          <Button
            variant="outline"
            onClick={() => {
              setShowRenameModal(true);
              setRenameDone(false);
              setRenamePreview([]);
              setRenameFolder('');
            }}
            title="Batch-rename a folder of spec files to a spec-number format"
          >
            <Icon icon={FilePenLine} em={1.1818} />
            Rename Files
          </Button>
        )}
      </div>

      {/* ── Two-pane cockpit: section table (left) + live Adding cart (right) ── */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
        {/* Far left — CSI division rail (same component as Master Specs) */}
        {divisions.length > 1 && (
          <DivisionRail
            divisions={railDivisions}
            total={pickableMasters.length}
            selected={divFilter}
            onSelect={(next) => setDivFilter(next)}
            compact={railCompact}
          />
        )}
        {/* Left — sections table */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            padding: 'var(--space-4) var(--space-2) var(--space-1) var(--space-5)',
          }}
        >
          {loadError && (
            <div
              role="alert"
              style={{
                fontSize: 'var(--font-size-base)',
                color: 'var(--dg)',
                padding: '0 0 var(--space-2)',
                flexShrink: 0,
              }}
            >
              {loadError}
            </div>
          )}
          {loading && masters.length === 0 ? (
            /* Loading skeleton — table silhouette while masters stream in */
            <div
              aria-hidden="true"
              className="animate-pulse"
              style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}
            >
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    height: i === 0 ? 40 : 36,
                    borderRadius: 'var(--radius-md)',
                    background: 'var(--sa)',
                  }}
                />
              ))}
            </div>
          ) : (
            <DataTable<SpecMaster>
              columns={columns}
              data={shownMasters}
              keyField="id"
              defaultGroupKey="csiNumber"
              onRowClick={(m) => toggleMaster(m.id)}
              selectedIds={selectedIdSet}
              onSelectionChange={onPickSelectionChange}
              permissions={userPermissions}
              bulkActions={{
                exportCsv: { permission: 'specs.export', filename: 'master-specs' },
              }}
              initialSort={{ key: 'csiNumber', direction: 'asc' }}
              searchPlaceholder="Search by section number or title…"
              storageKey="add-specs-masters"
              emptyIcon={'📚'}
              emptyTitle={divFilter ? 'No sections in this division' : 'No master specs'}
              emptyMessage={
                divFilter
                  ? 'Clear the division filter to see every section.'
                  : 'Create master specs in the Master Specs library first.'
              }
              groupOptions={[
                // Keyed to the 'csiNumber' COLUMN (DataTable merges by key)
                // — this used to be keyed 'division', which matched no
                // column and was silently dropped from the group menu.
                {
                  key: 'csiNumber',
                  label: 'Division #',
                  getValue: (m) => csiDivisionGroupLabel(m.csiNumber),
                },
              ]}
              toolbarExtra={
                <Button
                  variant="outline"
                  onClick={() => {
                    const shownIds = new Set(shownMasters.map((m) => m.id));
                    const addable = shownMasters.filter((m) => inProjectSpecs(m).length === 0);
                    const allShownSelected =
                      addable.length > 0 && addable.every((m) => isSelected(m.id));
                    if (allShownSelected) {
                      setSelectedMasters((prev) => prev.filter((s) => !shownIds.has(s.masterId)));
                    } else {
                      setSelectedMasters((prev) => {
                        const have = new Set(prev.map((s) => s.masterId));
                        return [
                          ...prev,
                          ...addable
                            .filter((m) => !have.has(m.id))
                            .map((m) => ({ masterId: m.id, mode: 'new' as AddMode })),
                        ];
                      });
                    }
                  }}
                >
                  {shownMasters.some((m) => inProjectSpecs(m).length === 0) &&
                  shownMasters
                    .filter((m) => inProjectSpecs(m).length === 0)
                    .every((m) => isSelected(m.id))
                    ? divFilter
                      ? 'Clear division'
                      : 'Clear all'
                    : divFilter
                      ? 'Select division'
                      : 'Select all'}
                </Button>
              }
            />
          )}
        </div>

        {/* Right — live "Adding to project" cart (fills what used to be dead space) */}
        <aside
          style={{
            width: 'clamp(' + rem(300) + ', 24vw, ' + rem(360) + ')',
            flexShrink: 0,
            borderLeft: '1px solid var(--bd)',
            background: 'var(--sf)',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          {/* Cart header */}
          <div
            style={{
              padding: 'var(--space-4) var(--space-4) var(--space-3)',
              borderBottom: '1px solid var(--bd)',
            }}
          >
            <div
              style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: rem(0.5),
                color: 'var(--tm)',
              }}
            >
              Adding to project
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 'var(--space-2)',
                marginTop: 'var(--space-1)',
              }}
            >
              <span style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700, lineHeight: 1.25 }}>
                {selectedMasters.length}
              </span>
              <span style={{ fontSize: 'var(--font-size-base)', color: 'var(--tm)' }}>
                section{selectedMasters.length === 1 ? '' : 's'}
                {replaceCount > 0 ? ` \u00b7 ${replaceCount} replacing` : ''}
              </span>
            </div>
          </div>

          {/* Forma — belongs with "what am I adding" */}
          <div
            style={{
              padding: 'var(--space-3) var(--space-4)',
              borderBottom: '1px solid var(--bd)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 'var(--space-2)',
              }}
            >
              <span
                style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--tm)' }}
              >
                Forma
              </span>
              {formaIsClientMatch && (
                <span style={badge('rgba(var(--acr),0.1)', 'var(--ac)')}>Client match</span>
              )}
            </div>
            <SearchableSelect
              value={selectedFormaId}
              options={formaOptions}
              onChange={(v) => setSelectedFormaId(v || '')}
              clearable
              searchPlaceholder="Search formas…"
              placeholder={standards.length === 0 ? 'No formas' : 'Choose a forma…'}
            />
          </div>

          {/* Cart body — selected sections + live smart-block resolution */}
          <div
            style={{
              flex: 1,
              overflow: 'auto',
              padding: 'var(--space-3) var(--space-4)',
              minHeight: 0,
            }}
          >
            {selectedMasters.length === 0 ? (
              <div
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  color: 'var(--tm)',
                  gap: 'var(--space-3)',
                  padding: '0 var(--space-3)',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: rem(40),
                    height: rem(40),
                    borderRadius: 'var(--radius-lg)',
                    background: 'var(--sa)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon icon={ListPlus} em={1.3846} />
                </span>
                <div
                  style={{ fontSize: 'var(--font-size-base)', lineHeight: 1.5, maxWidth: rem(240) }}
                >
                  Pick sections on the left — they stack up here, then{' '}
                  <strong style={{ color: 'var(--tx)' }}>Add</strong> forks them into this project.
                </div>
              </div>
            ) : (
              <>
                {/* Selected sections list */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--space-1)',
                    marginBottom: 'var(--space-4)',
                  }}
                >
                  {selectedMasters.map((sel) => {
                    const m = masters.find((x) => x.id === sel.masterId);
                    if (!m) return null;
                    return (
                      <div
                        key={sel.masterId}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-2)',
                          padding: 'var(--space-2)',
                          borderRadius: 'var(--radius-md)',
                          background: 'var(--bg)',
                        }}
                      >
                        <span
                          style={{
                            fontFamily: 'var(--font-mono)',
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--ac)',
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          {m.csiNumber}
                        </span>
                        <span
                          style={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: 'var(--font-size-base)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={m.title}
                        >
                          {m.title}
                        </span>
                        {sel.mode !== 'new' && (
                          <span
                            style={{
                              fontSize: 'var(--font-size-xs)',
                              color: 'var(--tm)',
                              flexShrink: 0,
                            }}
                          >
                            {sel.mode === 'replace' ? 'replaces existing' : 'as .01 copy'}
                          </span>
                        )}
                        <button
                          onClick={() => toggleMaster(m.id)}
                          title="Take off the list"
                          aria-label={`Take ${m.csiNumber || m.title} off the list`}
                          style={{
                            width: rem(32),
                            height: rem(32),
                            margin:
                              'calc(-1 * var(--space-2)) calc(-1 * var(--space-2)) calc(-1 * var(--space-2)) 0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: 'none',
                            background: 'transparent',
                            color: 'var(--tm)',
                            borderRadius: 'var(--radius-sm)',
                            cursor: 'pointer',
                            padding: 0,
                            flexShrink: 0,
                          }}
                        >
                          <Icon icon={X} em={1.0769} aria-hidden="true" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Cart footer — single primary action. Hand-rolled (not kit Button) so
              the guidance title still shows on hover while disabled. */}
          <div
            style={{ padding: 'var(--space-3) var(--space-4)', borderTop: '1px solid var(--bd)' }}
          >
            <button
              onClick={() => void addToProject()}
              disabled={selectedMasters.length === 0 || adding || !canAddSpecs}
              title={
                !canAddSpecs
                  ? "You don't have permission to compile specs into a project"
                  : selectedMasters.length === 0
                    ? 'Select at least one section'
                    : `Add ${selectedMasters.length} section${selectedMasters.length === 1 ? '' : 's'} to ${activeProject.name} and open the project`
              }
              style={{
                width: '100%',
                padding: 'var(--space-3) var(--space-5)',
                borderRadius: 'var(--radius-md)',
                border: 'none',
                background:
                  selectedMasters.length > 0 && !adding && canAddSpecs ? 'var(--ac)' : 'var(--sa)',
                color:
                  selectedMasters.length > 0 && !adding && canAddSpecs
                    ? 'var(--color-text-inverse)'
                    : 'var(--tm)',
                fontSize: 'var(--font-size-md)',
                fontWeight: 700,
                cursor:
                  selectedMasters.length > 0 && !adding && canAddSpecs ? 'pointer' : 'default',
                transition: FAST_COLORS,
              }}
            >
              {adding
                ? 'Adding…'
                : selectedMasters.length > 0
                  ? `Add ${selectedMasters.length} to ${activeProject.name?.split(' ')[0] || 'Project'}`
                  : 'Add to Project'}
            </button>
          </div>
        </aside>
      </div>
      {showRenameModal && renameModal}

      {assembleFor && (
        <AssembleModulesDialog
          open
          onOpenChange={(o) => !o && setAssembleFor(null)}
          specs={assembleFor.ids.flatMap((id) => {
            const m = masters.find((x) => x.id === id);
            return m
              ? [
                  {
                    id: m.id,
                    number: m.csiNumber || '',
                    title: m.title || 'Untitled',
                    blocks: visibleComposedBlocks(m, layerFor(m)),
                  },
                ]
              : [];
          })}
          initialPicks={modulePick}
          userPermissions={userPermissions}
          toast={toast}
          onDone={(picks) => {
            const next = { ...modulePick, ...picks };
            const thenAdd = assembleFor.thenAdd;
            setModulePick(next);
            setAssembleFor(null);
            if (thenAdd) void runAddToProject(next);
          }}
        />
      )}

      {/* Section number already in the project: Replace / Add as .01 / Cancel */}
      <Dialog open={!!inProjectFor} onOpenChange={(o) => !o && setInProjectFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{inProjectFor?.csiNumber || inProjectFor?.title || 'Section'}</DialogTitle>
            <DialogDescription>
              This section number is already in the project. Replace archives the current copy
              (restorable) and adds a fresh copy of the master.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="items-center justify-between space-x-0">
            <Button
              variant="destructive"
              disabled={!canAddSpecs || !canArchiveSpecs}
              title={
                !canAddSpecs || !canArchiveSpecs
                  ? 'Replace needs permission to add and to archive project specs'
                  : 'Archive the copy in the project and add a fresh one in its place'
              }
              onClick={() => chooseInProject('replace')}
            >
              Replace
            </Button>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setInProjectFor(null)}>
                Cancel
              </Button>
              <Button
                variant="outline"
                disabled={!canAddSpecs}
                onClick={() => chooseInProject('dup')}
              >
                Add as .01
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Shared styles ───
function badge(bg: string, color: string): React.CSSProperties {
  return {
    fontSize: 11,
    padding: '2px var(--space-2)',
    borderRadius: 'var(--radius-sm)',
    background: bg,
    color,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  };
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'rgba(0, 0, 0, 0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};
const modalStyle: React.CSSProperties = {
  background: 'var(--sf)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-5)',
  width: rem(560),
  border: '1px solid var(--bd)',
  boxShadow: 'var(--shadow-xl)',
  maxHeight: '80vh',
  overflow: 'auto',
};

(window as any).AddSpecs = AddSpecs;
export { AddSpecs };
